import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react"
import posthog, { isPostHogConfigured } from "../posthog"
import { supabase } from "../utils/supabase"

const ProfileCtx = createContext(null)

export function ProfileProvider({ children }) {
	const [session, setSession] = useState(null)
	const [profile, setProfile] = useState(null)
	const [loading, setLoading] = useState(true)
	const identifiedUserId = useRef(null)

	const userId = session?.user?.id
	const analyticsUserId = session?.user?.is_anonymous ? null : userId

	useEffect(() => {
		if (!isPostHogConfigured) return

		const currentAnalyticsUserId = identifiedUserId.current || posthog.get_property('$user_id')

		if (!analyticsUserId) {
			if (currentAnalyticsUserId) {
				posthog.reset()
				identifiedUserId.current = null
			}
			return
		}

		if (currentAnalyticsUserId && currentAnalyticsUserId !== analyticsUserId) {
			posthog.reset()
			identifiedUserId.current = null
		}

		if (identifiedUserId.current !== analyticsUserId) {
			const personProperties = {}
			if (session.user.email) personProperties.email = session.user.email

			posthog.identify(analyticsUserId, personProperties)
			identifiedUserId.current = analyticsUserId
		}
	}, [analyticsUserId, session?.user?.email])

	useEffect(() => {
		let alive = true

		const init = async () => {
			const { data } = await supabase.auth.getSession()
			if (!alive) return
			setSession(data.session)
		}

		const { data: sub } = supabase.auth.onAuthStateChange((_evt, newSession) => {
			setSession(newSession)
			if (!newSession) {
				setProfile(null)
			}
		})

		init()
		return () => {
			alive = false
			sub.subscription.unsubscribe()
		}
	}, [])

	const fetchProfile = async (uid) => {
		setLoading(true)
		try {
			const { data, error } = await supabase
				.from("profiles")
				.select("id, full_name, role")
				.eq("id", uid)
				.maybeSingle()
			if (error) throw error
			setProfile(data || null)
		} finally {
			setLoading(false)
		}
	}

	// Load on login / user change
	useEffect(() => {
		if (!userId) {
			setProfile(null)
			setLoading(false)
			return
		}
		fetchProfile(userId)
	}, [userId])

	useEffect(() => {
		if (!isPostHogConfigured || !analyticsUserId || profile?.id !== analyticsUserId) return

		const personProperties = {}
		if (profile.full_name) personProperties.name = profile.full_name
		if (profile.role) personProperties.role = profile.role

		if (Object.keys(personProperties).length > 0) {
			posthog.setPersonProperties(personProperties)
		}
	}, [profile, analyticsUserId])

	// Realtime keep-in-sync
	useEffect(() => {
		if (!userId) return
		const channel = supabase
			.channel("realtime:profiles:" + userId)
			.on(
				"postgres_changes",
				{ event: "*", schema: "public", table: "profiles", filter: `id=eq.${userId}` },
				() => fetchProfile(userId)
			)
			.subscribe()

		return () => {
			supabase.removeChannel(channel)
		}
	}, [userId])

	const value = useMemo(
		() => ({
			session,
			user: session?.user ?? null,
			profile,
			role: profile?.role ?? null,
			loading,
			refresh: () => userId && fetchProfile(userId),
			updateProfile: async (patch) => {
				if (!userId) return
				await supabase.from("profiles").upsert({ id: userId, ...patch }, { onConflict: "id" })
				fetchProfile(userId)
			},
		}),
		[session, profile, loading, userId]
	)

	return <ProfileCtx.Provider value={value}>{children}</ProfileCtx.Provider>
}

export function useProfile() {
	return useContext(ProfileCtx)
} 